<script setup lang="ts">
import { ref, computed } from 'vue'
import { Globe, Lock, LogOut, ChevronRight } from 'lucide-vue-next'
import LanguageSelector from '@/components/common/LanguageSelector.vue'
import { useI18nStore } from '@99fun-agent/stores'

const emit = defineEmits(['close', 'logout', 'open-change-password', 'change-lang'])

const i18nStore = useI18nStore()
const isLangOpen = ref(false)

// 当前语言显示名称
const currentLangName = computed(() => {
  // 获取当前语言代码
  const currentCode = i18nStore.appLang

  // console.log('🔍 currentLangName - currentCode:', currentCode)
  // console.log('🔍 currentLangName - currentLanguageInfo.value:', currentLanguageInfo.value)
  // console.log('🔍 currentLangName - supportedLanguages.value:', supportedLanguages.value)

  // 方法1: 尝试从 currentLanguageInfo 获取
  const info = i18nStore.currentLanguageInfo
  if (info?.name) {
    return info.name
  }

  // 方法2: 从 supportedLanguages 中查找
  if (i18nStore.supportLanguageList) {
    const lang = i18nStore.supportLanguageList.find(l => l.key === currentCode)
    console.log('🔍 currentLangName - found lang:', lang)
    if (lang?.name) {
      return lang.name
    }
  }

  return currentCode
})

// 处理语言选择并自动关闭菜单
const handleLanguageSelect = (langCode: string) => {
  // emit('change-lang', langCode)
  emit('close')
}
</script>

<template>
  <div class="fixed inset-0 z-[199]" @click="emit('close')"></div>

  <div class="absolute right-0 top-8 w-56 bg-white rounded-[20px] shadow-[0_10px_40px_rgba(0,0,0,0.1)] border border-gray-50 z-[200] py-2 overflow-hidden animate-in fade-in zoom-in duration-200">
    
    <div class="px-1">
      <div
        @click="isLangOpen = !isLangOpen"
        class="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer rounded-xl group"
      >
        <div class="flex items-center gap-3">
          <Globe :size="18" class="text-gray-600" />
          <span class="text-[14px] font-medium text-gray-700">{{ $t('language') }}</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="text-[12px] text-gray-400">{{ currentLangName }}</span>
          <ChevronRight :size="14" :class="['text-gray-300 transition-transform', isLangOpen ? 'rotate-90' : '']" />
        </div>
      </div>

      <div v-if="isLangOpen" class="mb-2">
        <LanguageSelector
          @select="handleLanguageSelect"
          :container-class="''"
        >
          <template #item="{ lang, isSelected }">
            <div class="relative px-4 py-2.5 cursor-pointer group">
              <div
                v-if="isSelected"
                class="absolute inset-x-1 inset-y-0.5 bg-gradient-to-r from-[#F5F3FF] to-[#EDE9FE] rounded-lg -z-10"
              ></div>

              <div class="flex items-center justify-between gap-2 relative z-10">
                <span :class="[
                  'text-[14px] transition-colors',
                  isSelected ? 'text-[#2B6DFF] font-bold' : 'text-gray-500 font-medium'
                ]">
                  {{ lang.name }}
                </span>
                <span :class="[
                  'text-[11px] opacity-70 whitespace-nowrap',
                  isSelected ? 'text-[#2B6DFF]' : 'text-gray-400'
                ]">
                  {{ lang.nativeName }}
                </span>
              </div>
            </div>
          </template>
        </LanguageSelector>
      </div>
    </div>

    <div class="h-[1px] bg-gray-50 mx-4 my-1"></div>

    <div class="px-1">
      <div 
        @click="emit('open-change-password')"
        class="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer rounded-xl"
      >
        <Lock :size="18" class="text-gray-600" />
        <span class="text-[14px] font-medium text-gray-700">{{ $t('edit_pwd') }}</span>
      </div>
    </div>

    <div class="h-[1px] bg-gray-50 mx-4 my-1"></div>

    <div class="px-1">
      <div 
        @click="emit('logout')"
        class="flex items-center gap-3 px-4 py-3 hover:bg-red-50 cursor-pointer rounded-xl group text-red-500"
      >
        <LogOut :size="18" />
        <span class="text-[14px] font-bold">{{ $t('logout') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.animate-in {
  animation: dropdownIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes dropdownIn {
  from { opacity: 0; transform: translateY(-8px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
</style>